import React, { useState, useEffect } from 'react';
import { Loader2, Copy, RefreshCw, Save, FolderOpen, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import api from '../lib/api';

function CopyRow({ label, value, testid }) {
    return (
        <div className="flex items-start gap-2 py-1.5">
            <div className="min-w-[160px] text-sm font-medium text-brand-text-secondary pt-2">{label}</div>
            <div className="flex-1 relative">
                <Input readOnly value={value || ''} className="font-mono text-xs bg-brand-accent-soft/30 pr-10" data-testid={testid} />
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1 h-8 w-8"
                    onClick={() => {
                        if (!value) return;
                        navigator.clipboard.writeText(value);
                        toast.success(`${label} disalin`);
                    }}
                    data-testid={`${testid}-copy`}
                >
                    <Copy className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
}


export default function FileSettingTab({ companyId }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rotating, setRotating] = useState(false);
    const [nextcloudUrl, setNextcloudUrl] = useState('');
    const [providerId, setProviderId] = useState(1);
    const [enabled, setEnabled] = useState(true);
    const [oidc, setOidc] = useState(null);
    const [savedUrl, setSavedUrl] = useState('');

    const load = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/companies/${companyId}/nextcloud`);
            if (res.data?.config) {
                setNextcloudUrl(res.data.config.nextcloud_url || '');
                setEnabled(res.data.config.enabled !== false);
                setProviderId(res.data.config.provider_id || 1);
                setSavedUrl(res.data.config.nextcloud_url || '');
            }
            setOidc(res.data?.oidc || null);
        } catch (err) {
            console.error('Failed to load NC config:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (companyId) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    const handleSave = async () => {
        if (!nextcloudUrl.trim()) {
            toast.error('URL Nextcloud wajib diisi');
            return;
        }
        const pid = parseInt(providerId, 10);
        if (!pid || pid < 1) {
            toast.error('Provider ID harus angka ≥ 1');
            return;
        }
        try {
            setSaving(true);
            const res = await api.put(`/companies/${companyId}/nextcloud`, {
                nextcloud_url: nextcloudUrl.trim(),
                enabled,
                provider_id: pid,
            });
            toast.success('Pengaturan disimpan');
            setSavedUrl(res.data?.config?.nextcloud_url || nextcloudUrl);
            setProviderId(res.data?.config?.provider_id || pid);
            setOidc(res.data?.oidc || null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal menyimpan');
        } finally {
            setSaving(false);
        }
    };

    const handleRotate = async () => {
        if (!window.confirm('Rotasi secret akan membatalkan client_id/secret yang lama. Nextcloud harus di-re-konfigurasi. Lanjutkan?')) return;
        try {
            setRotating(true);
            const res = await api.post(`/companies/${companyId}/nextcloud/rotate-secret`);
            toast.success('Client secret dirotasi. Update juga di Nextcloud!');
            setOidc(res.data?.oidc || null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal rotasi');
        } finally {
            setRotating(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-10">
                <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="file-setting-tab">
            {/* Config Card */}
            <Card className="border-brand-border shadow-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                        <FolderOpen className="w-5 h-5 text-brand-primary" />
                        Pengaturan File (Nextcloud)
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label>URL Nextcloud</Label>
                        <Input
                            placeholder="https://file.perusahaan-anda.com"
                            value={nextcloudUrl}
                            onChange={(e) => setNextcloudUrl(e.target.value)}
                            data-testid="nc-url-input"
                        />
                        <p className="text-xs text-brand-text-muted">
                            Base URL instansi Nextcloud perusahaan Anda. Tanpa slash di akhir.
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label>Provider ID (user_oidc)</Label>
                        <Input
                            type="number"
                            min="1"
                            placeholder="1"
                            value={providerId}
                            onChange={(e) => setProviderId(e.target.value)}
                            data-testid="nc-provider-id-input"
                        />
                        <p className="text-xs text-brand-text-muted">
                            Nomor provider OIDC di Nextcloud (biasanya <code>1</code> bila TaskGlory adalah provider pertama).
                            Dipakai untuk auto-login saat buka menu File tanpa melihat halaman login NC.
                            Cek di: <em>Administration → OpenID Connect</em> — ID tiap provider tertera di sana.
                        </p>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-brand-accent-soft/30 rounded-lg">
                        <div>
                            <div className="text-sm font-medium">Aktifkan menu File</div>
                            <div className="text-xs text-brand-text-muted">
                                Bila dimatikan, menu "File" tidak muncul di sidebar anggota.
                            </div>
                        </div>
                        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="nc-enabled-switch" />
                    </div>
                    <div className="flex gap-2 pt-2">
                        <Button onClick={handleSave} disabled={saving} data-testid="nc-save-btn">
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            Simpan
                        </Button>
                        {savedUrl && (
                            <Button variant="outline" onClick={() => window.open(savedUrl, '_blank', 'noopener')}>
                                <ExternalLink className="w-4 h-4 mr-2" /> Buka Nextcloud
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* OIDC credentials */}
            {oidc && (
                <Card className="border-brand-border shadow-card">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center justify-between">
                            <span className="flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                Kredensial OIDC untuk Nextcloud
                            </span>
                            <Button size="sm" variant="outline" onClick={handleRotate} disabled={rotating} data-testid="nc-rotate-btn">
                                {rotating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                Rotasi Secret
                            </Button>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <CopyRow label="Discovery URL" value={oidc.discovery_url} testid="nc-discovery-url" />
                        <CopyRow label="Client ID" value={oidc.client_id} testid="nc-client-id" />
                        <CopyRow label="Client Secret" value={oidc.client_secret} testid="nc-client-secret" />
                        <CopyRow label="Redirect URI" value={(oidc.redirect_uris || [])[0]} testid="nc-redirect-uri" />
                    </CardContent>
                </Card>
            )}

            {/* Tutorial */}
            <Card className="border-brand-border shadow-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        Tutorial Setup di Sisi Nextcloud
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                        <strong>Prasyarat:</strong> akun admin Nextcloud + akses ke menu <em>Apps</em> & <em>Settings → OpenID Connect</em>.
                    </div>

                    <div>
                        <div className="font-semibold mb-1">1. Install app "OpenID Connect user backend" (user_oidc)</div>
                        <ol className="list-decimal list-inside space-y-1 text-brand-text-secondary pl-2">
                            <li>Login Nextcloud sebagai admin</li>
                            <li>Buka <em>klik profil → Apps → Integration</em></li>
                            <li>Cari <strong>"OpenID Connect user backend"</strong> → klik <strong>Download and enable</strong></li>
                        </ol>
                    </div>

                    <div>
                        <div className="font-semibold mb-1">2. Daftarkan provider baru</div>
                        <ol className="list-decimal list-inside space-y-1 text-brand-text-secondary pl-2">
                            <li>Buka <em>Administration settings → OpenID Connect</em></li>
                            <li>Klik <strong>"Register new provider"</strong>. Isi:</li>
                        </ol>
                        <div className="mt-2 p-3 bg-brand-accent-soft/30 rounded-lg space-y-1 text-xs font-mono">
                            <div><strong>Identifier:</strong> TaskGlory</div>
                            <div><strong>Client ID:</strong> (dari kotak di atas)</div>
                            <div><strong>Client secret:</strong> (dari kotak di atas)</div>
                            <div><strong>Discovery endpoint:</strong> (dari kotak di atas)</div>
                            <div><strong>Scope:</strong> openid profile email</div>
                            <div><strong>Attribute mapping:</strong></div>
                            <ul className="ml-4 space-y-0.5">
                                <li>• User ID mapping: <code>sub</code></li>
                                <li>• Display name mapping: <code>name</code></li>
                                <li>• Email mapping: <code>email</code></li>
                                <li>• Quota mapping: <em>(kosongkan)</em></li>
                            </ul>
                        </div>
                    </div>

                    <div>
                        <div className="font-semibold mb-1">3. Pengaturan Lanjutan — WAJIB untuk auto-login mulus</div>
                        <ul className="list-disc list-inside space-y-1 text-brand-text-secondary pl-2">
                            <li>Aktifkan <strong>"Use unique user ID"</strong></li>
                            <li>Aktifkan <strong>"Auto-provision users"</strong> — user baru akan otomatis dibuat di Nextcloud saat login pertama.</li>
                            <li><strong className="text-amber-700">Aktifkan "Soft auto-redirect"</strong> — halaman login Nextcloud akan <em>otomatis</em> redirect ke TaskGlory OIDC tanpa perlu user klik tombol "Log in with TaskGlory". Kombinasi ini + Provider ID yang benar membuat menu File terasa seamless (langsung buka tanpa layar login NC).</li>
                        </ul>
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                            <strong>Catat Provider ID:</strong> setelah "Register new provider" berhasil, Nextcloud akan menampilkan daftar provider dengan <strong>ID numerik</strong> (1, 2, 3, ...). Masukkan angka itu pada kolom <em>Provider ID</em> di form di atas.
                        </div>
                    </div>

                    <div>
                        <div className="font-semibold mb-1">4. Uji Coba</div>
                        <ol className="list-decimal list-inside space-y-1 text-brand-text-secondary pl-2">
                            <li>Logout dari Nextcloud</li>
                            <li>Pada halaman login NC akan muncul tombol <strong>"Log in with TaskGlory"</strong></li>
                            <li>Klik → diarahkan ke aplikasi kita → otomatis redirect kembali ke NC dan user sudah masuk</li>
                            <li>Setelah verifikasi berhasil, user di aplikasi cukup klik menu <strong>File</strong> dan langsung login</li>
                        </ol>
                    </div>

                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                        <strong>Catatan:</strong> User di Nextcloud akan ter-provision dengan ID sama seperti di aplikasi. Email & nama akan di-sync otomatis pada setiap login.
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
